import * as Sentry from '@sentry/nextjs';
import { ERROR_MESSAGES } from '@/constants/events';
import { type BasketNotFoundSource } from '@/types/orderStatus';
import { getDuplicateAccountKind } from '@/utils/errors';

/**
 * Dispatch a captured message to Sentry.
 */
export function captureMessage(message: string, level?: Sentry.SeverityLevel, extra = {}) {
  if (typeof window !== 'undefined') {
    console.warn(message, { level, extra });
    Sentry.captureMessage(message, { level, extra });
  }
}

/**
 * Dispatch a captured exception to Sentry. `tags` are indexed by Sentry, so — unlike `extra` — they
 * can be searched and grouped on (e.g. `missing_fields` on the delivery-address guard, TEAM-15164).
 */
export function captureException(
  exception: unknown,
  level?: Sentry.SeverityLevel,
  extra = {},
  tags?: Record<string, string>,
) {
  if (typeof window !== 'undefined') {
    Sentry.captureException(exception, { level, extra, tags });
  } else {
    console.error(exception);
  }
}

/**
 * Loose view of the `prepareErrorMessage` result (`{ message, error }`) that the
 * auth sagas hand to reportAuthFailure. Every field is optional so partial inputs
 * — including a bare `null` — are safe to pass.
 */
type AuthFailureResponse = {
  message?: string | null;
  error?: { error?: string | null; error_description?: string | null } | null;
} | null;

/**
 * Expected outcome, not a bug — so it is kept out of the Issues view. Shares one implementation with
 * the diner-facing toast in `auth/saga.ts` (TEAM-13882); it reads the raw `error_description` because
 * the resolved diner copy no longer carries the backend's wording.
 */
function isDuplicateAccountFailure(errorResponse?: AuthFailureResponse): boolean {
  return getDuplicateAccountKind(errorResponse?.error?.error_description) !== null;
}

/**
 * Returns true when the failure is an expected, non-bug auth outcome that should
 * not surface in Sentry's Issues view — a user-credential mismatch (the OAuth
 * server rejected the supplied email/password) or a duplicate-account rejection
 * at sign-up / checkout (the email or phone is already registered).
 */
export function isExpectedAuthFailure(
  exception: unknown,
  errorResponse?: AuthFailureResponse,
): boolean {
  if (errorResponse?.error?.error === 'invalid_credentials') {
    return true;
  }
  if (isDuplicateAccountFailure(errorResponse)) {
    return true;
  }
  if (typeof exception === 'object' && exception !== null) {
    const e = exception as { status?: number; json?: { error?: string } };
    if (e.status === 401) return true;
    if (e.json?.error === 'invalid_credentials') return true;
  }
  return false;
}

/**
 * Report an auth-flow failure. Wrong-password / invalid_credentials results
 * are logged to Sentry at info level so they remain queryable for anomaly
 * detection without polluting the Issues view. Anything else falls through
 * to a regular exception capture.
 */
export function reportAuthFailure(
  exception: unknown,
  errorResponse: AuthFailureResponse | undefined,
  extra: Record<string, unknown> = {},
) {
  if (isExpectedAuthFailure(exception, errorResponse)) {
    const eventName = typeof extra.eventName === 'string' ? extra.eventName : 'Auth Failure';
    captureMessage(`${eventName} (expected)`, 'info', { ...extra, details: errorResponse });
    return;
  }
  captureException(exception, 'error', { ...extra, details: errorResponse });
}

export const REGISTER_URL_HINT = '/v1/ordering/loyalty/register';
export const LOGIN_URL_HINT = '/oauth/access_token';
export const FORGOT_PASSWORD_URL_HINT = '/v1/ordering/loyalty/forgot-password';
export const BASKET_VALIDATE_URL_HINT =
  '/v1/ordering/store-locations/{locationId}/baskets/{basketId}/validate';

/**
 * The two diner-facing sentences for "the request never got a readable response"
 * (TEAM-14320). These sentences — not the joined string below — are the shared unit,
 * because the checkout recovery modal needs them in two separate fields (a title and a
 * body that continues into its own cart/charge reassurance) rather than one blob. Any
 * surface showing this failure class composes from these constants so the wording can
 * only be changed in one place.
 */
export const CONNECTION_RETRY_TITLE = "We're having trouble connecting.";
export const CONNECTION_RETRY_ACTION = 'Please check your connection and try again.';
/** The retry affordance's label, shared by every connection-error surface. */
export const CONNECTION_RETRY_LABEL = 'Try again';

/**
 * The banner form of the copy above: rendered verbatim by `ConnectionErrorState`'s banner
 * variant (`components/connectionError`) on sign-up, login, forgot-password and the rewards
 * page. The literal `\n` is load-bearing — the banner sets `whiteSpace: 'pre-line'` and the
 * sign-up test asserts this string un-normalized, so the exact bytes are pinned by
 * `components/connectionError/banner-contract.test.tsx`.
 */
export const CONNECTION_RETRY_COPY = `${CONNECTION_RETRY_TITLE}\n${CONNECTION_RETRY_ACTION}`;

/** @deprecated Prefer {@link CONNECTION_RETRY_COPY} — the copy is no longer register-specific. */
export const REGISTER_CONNECTION_RETRY_COPY = CONNECTION_RETRY_COPY;

/**
 * TEAM-14291: shared hold-release copy for Toast post-authorization failures.
 * Used by the saga (inline status bar), PlaceOrderError (page-error modal),
 * and any future surface that needs to tell the diner the hold clears on its own.
 *
 * `TOAST_HOLD_RELEASE_NOTE` is the append-friendly sentence (leading space).
 * `TOAST_HOLD_RELEASE_WITH_RETRY_WARNING` adds the second-hold caution for
 * surfaces where the diner has a retry affordance.
 */
export const TOAST_HOLD_RELEASE_NOTE =
  ' The hold on your card will be released automatically — your bank may take a few days to remove it.';

export const TOAST_HOLD_RELEASE_WITH_RETRY_WARNING = `${TOAST_HOLD_RELEASE_NOTE} If you try again, a new temporary hold may appear.`;

export interface ConnectionFailureContext {
  route?: string;
  urlHint?: string;
  basketId?: string;
  locationId?: string;
}

/**
 * Capture a structured Sentry event for a connection-level failure on a diner-facing
 * write path (the browser fetch threw before a response was received). Shape mirrors
 * reportNonJsonError so the COO failure family is filterable in Discover.
 *
 * `errorClass` keeps each surface separately filterable while sharing the tag/context
 * shape; the register variant below preserves the exact tag the existing Sentry saved
 * searches and `errorReporter.test.ts` are written against.
 */
export function reportConnectionFailure(
  error: unknown,
  errorClass: string,
  ctx: ConnectionFailureContext = {},
) {
  if (typeof window === 'undefined') {
    console.error('Connection failure', errorClass, error, ctx);
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('error_class', errorClass);
    scope.setTag('coo_api', 'true');
    if (ctx.route) scope.setTag('route', ctx.route);
    if (ctx.basketId) scope.setTag('basket_id', ctx.basketId);
    if (ctx.locationId) scope.setTag('location_id', ctx.locationId);

    scope.setContext(errorClass, {
      request_url: ctx.urlHint,
    });

    Sentry.captureException(error);
  });
}

export function reportRegisterConnectionFailure(
  error: unknown,
  ctx: ConnectionFailureContext = {},
) {
  reportConnectionFailure(error, 'register_connection_failure', ctx);
}

export function reportLoginConnectionFailure(error: unknown, ctx: ConnectionFailureContext = {}) {
  reportConnectionFailure(error, 'login_connection_failure', ctx);
}

export function reportForgotPasswordConnectionFailure(
  error: unknown,
  ctx: ConnectionFailureContext = {},
) {
  reportConnectionFailure(error, 'forgot_password_connection_failure', ctx);
}

/**
 * Report a Google Maps SDK failure so it stays visible in Sentry.
 *
 * The Sentry client config denyUrls-filters `https://maps.googleapis.com`, so an error thrown from
 * inside Google's JS — e.g. `InvalidValueError: <gmp-advanced-marker>` when an AdvancedMarker is
 * attached to a map from a different `google.maps` load (TEAM-13351) — is dropped even when a React
 * error boundary reports it, because denyUrls runs before `beforeSend` and can't be rescued
 * downstream. Capturing a fresh Error constructed here gives the event a stack whose top frame is
 * our own code, so it clears the filter; the original message/stack is preserved in the context.
 */
export function reportGoogleMapsError(error: unknown, ctx: { component?: string } = {}) {
  const detail = getErrorMessage(error);

  if (typeof window === 'undefined') {
    console.error('Google Maps failure', detail, ctx);
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('error_class', 'google_maps_load');
    if (ctx.component) scope.setTag('map_component', ctx.component);
    scope.setContext('google_maps_failure', { detail });
    Sentry.captureException(new Error(`Google Maps failure: ${detail}`));
  });
}

export interface StoreLocationFetchFailureContext {
  /** The store location id the fetch was for. */
  locationId?: number | string | null;
  /** The organization (brand) the store belongs to, for per-partner attribution. */
  organizationId?: number | string | null;
  /** HTTP status of the failing response (401 unauthorized / 404 not_found). */
  status?: number;
  /** The request URL, for the context block. */
  urlHint?: string;
  /** coo-api's `error` slug from the response body (`unauthorized` / `not_found`). */
  errorSlug?: string | null;
}

/**
 * Capture a store-location fetch failure (`GET /v1/ordering/store-locations/{id}`) with
 * enough context to attribute it to a partner, and split it into its own Sentry issue
 * (TEAM-14521).
 *
 * Two things this does that a plain `captureException` does not:
 * - **Attribution (AC5):** tags the store `location_id` and `organization_id` so the event
 *   maps to a named partner instead of only carrying the raw response body.
 * - **Its own group (AC7):** sets an explicit `fingerprint`. A tag alone does *not* re-group —
 *   these throws originate inside the SDK's `replayRequest`, so without a fingerprint they merge
 *   into the shared SDK issue (this is why `reportGoogleMapsError` captures a fresh Error). The
 *   fingerprint keys the group on the failure class + status, mirroring
 *   `chunkLoadErrorHandler`'s `setFingerprint` approach.
 *
 * The SDK swallows the first 401 (token refresh + one replay) and only rethrows the second
 * consecutive 401, so any 401 reaching the caller is the post-replay failure — recorded via the
 * `token_replay_exhausted` tag (AC6).
 */
export function reportStoreLocationFetchFailure(
  error: unknown,
  ctx: StoreLocationFetchFailureContext = {},
) {
  if (typeof window === 'undefined') {
    console.error('Store location fetch failure', error, ctx);
    return;
  }

  const status = typeof ctx.status === 'number' ? ctx.status : undefined;

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('error_class', 'store_location_fetch_failure');
    scope.setTag('coo_api', 'true');
    if (ctx.locationId != null) scope.setTag('location_id', String(ctx.locationId));
    if (ctx.organizationId != null) scope.setTag('organization_id', String(ctx.organizationId));
    if (status != null) scope.setTag('response_status', String(status));
    if (ctx.errorSlug) scope.setTag('error_slug', ctx.errorSlug);
    // A 401 here is by construction the second consecutive 401 (the SDK already refreshed the
    // token and replayed once); tag it so it is distinguishable from a first-attempt failure.
    scope.setTag('token_replay_exhausted', String(status === 401));

    scope.setContext('store_location_fetch_failure', {
      request_url: ctx.urlHint,
      response_status: status,
      error_slug: ctx.errorSlug,
    });

    // Force this signature into its own Sentry issue instead of the shared replayRequest group.
    scope.setFingerprint(['store_location_fetch_failure', String(status ?? 'unknown')]);

    Sentry.captureException(error);
  });
}

export interface BasketNotFoundContext {
  /** The checkout basket coo-api answered 404 for. */
  basketId: string;
  /** The store location the basket belongs to. */
  locationId: number | string;
  /** The organization (brand) the store belongs to — the Spike's org-mismatch dimension. */
  organizationId?: number | string | null;
  /** Which basket call answered 404. */
  source: BasketNotFoundSource;
}

/**
 * Capture a basket coo-api answered 404 for, once, as its own Sentry issue (TEAM-15690).
 *
 * Shaped like `reportStoreLocationFetchFailure`. The tags put the store and the brand on a single
 * attributable event, which is the data Spike TEAM-15705 needs to test its hypothesis that coo-api
 * fails to resolve the basket's location when the location's org does not match the token's. The
 * fingerprint keeps every source in one group: before this, the same 404 was split across
 * PAYMENT_TYPES_GET_ERROR, SUPPORTED_PAYMENT_TYPES_RETRY_EXHAUSTED, BASKET_VALIDATION_ERROR,
 * INIT_CHECKOUT_UNHANDLED_FAILURE and SUBMIT_CHECKOUT_ERROR, and the source tag still tells them
 * apart.
 */
export function reportBasketNotFound(error: unknown, ctx: BasketNotFoundContext) {
  if (typeof window === 'undefined') {
    console.error('Basket not found', error, ctx);
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('error_class', 'basket_not_found');
    scope.setTag('coo_api', 'true');
    scope.setTag('location_id', String(ctx.locationId));
    if (ctx.organizationId != null) scope.setTag('organization_id', String(ctx.organizationId));
    scope.setTag('basket_not_found_source', ctx.source);
    scope.setExtra('eventName', ERROR_MESSAGES.BASKET_NOT_FOUND);

    // The basket id is a context field, not a tag: it is unique per diner, so it would only add
    // one tag value per event.
    scope.setContext('basket_not_found', {
      basket_id: ctx.basketId,
      location_id: ctx.locationId,
      organization_id: ctx.organizationId ?? null,
      source: ctx.source,
    });

    scope.setFingerprint(['basket_not_found']);

    Sentry.captureException(error);
  });
}

/** Tag the brand name in Sentry and set up any extra event listeners. */
export function initialize(brand: string) {
  // Apply the brand name tag to events.
  Sentry.setTag('brand', brand);

  /**
   * Listens for Google Maps authentication errors.
   * For some insane reason, Google doesn't provide additional context
   * to this error handler so all we know is that something failed.
   * @see https://developers.google.com/maps/documentation/javascript/events#auth-errors
   */
  window.gm_authFailure = function () {
    captureMessage('Google Maps Authentication Error!', 'fatal');
  };
}

/**
 * Safely extracts error message from unknown error type
 * Includes stack trace and cause if available
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const parts = [error.message];

    if (error.stack) {
      parts.push(`Stack: ${error.stack}`);
    }

    if (error.cause) {
      parts.push(`Cause: ${String(error.cause)}`);
    }

    return parts.join(' | ');
  }
  if (typeof error === 'string') {
    return error;
  }
  return String(error);
};
